import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { User } from 'firebase/auth';
import {
  createHostedServer,
  fetchHostedServer,
  HostedActualServer,
  startHostedServer,
  touchHostedServer
} from '../utils/hostingApi';
import { isFeatureEnabled } from '../utils/featureFlags';

const HOSTED_SERVER_QUERY_KEY = 'hostedServer';

export function useHostedServer(user: User | null) {
  const queryClient = useQueryClient();
  const enabled = Boolean(user) && isFeatureEnabled('ENABLE_ACTUAL_HOSTING');

  const query = useQuery<HostedActualServer | null>({
    queryKey: [HOSTED_SERVER_QUERY_KEY, user?.uid],
    queryFn: fetchHostedServer,
    enabled,
    refetchInterval: (queryState) =>
      queryState.state.data?.status === 'provisioning' ? 3000 : false
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: [HOSTED_SERVER_QUERY_KEY, user?.uid] });

  const createMutation = useMutation({
    mutationFn: createHostedServer,
    onSuccess: (result) => {
      queryClient.setQueryData([HOSTED_SERVER_QUERY_KEY, user?.uid], result.server);
      invalidate();
    }
  });

  const startMutation = useMutation({
    mutationFn: startHostedServer,
    onSuccess: invalidate
  });

  const touchMutation = useMutation({
    mutationFn: touchHostedServer,
    onSuccess: (server) => {
      queryClient.setQueryData([HOSTED_SERVER_QUERY_KEY, user?.uid], server);
    }
  });

  const isBusy =
    createMutation.isPending ||
    startMutation.isPending;

  return {
    server: query.data ?? null,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    isBusy,
    createServer: createMutation.mutateAsync,
    startServer: startMutation.mutateAsync,
    touchServer: () => touchMutation.mutateAsync(),
    refetch: query.refetch
  };
}
